import {JARDINIER_ACTIONS_REALISEES_MAX_LENGTH, JARDINIER_FIELD_MAX_LENGTH} from "@mytour/shared";
import type {Control, FieldErrors} from "react-hook-form";
import {Controller} from "react-hook-form";
import {FormGroup, Input, Label} from "reactstrap";
import {z} from "zod";

export const step1Schema = z.object({
  etatPotagerArrivee: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
  actionsRealisees: z.string().max(JARDINIER_ACTIONS_REALISEES_MAX_LENGTH).optional(),
  legumesRecolteEtPlantations: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
  observationsComplementaires: z.string().max(JARDINIER_FIELD_MAX_LENGTH).optional(),
  arrosageVerifie: z.boolean().default(false),
});

export type Step1Form = z.infer<typeof step1Schema>;

type Props = {
  control: Control<Step1Form>;
  errors: FieldErrors<Step1Form>;
  disabled: boolean;
};

export function Step1Potager({control, errors, disabled}: Props) {
  return (
    <>
      <h5 style={{marginBottom: "12px"}}>Potager</h5>

      <TextareaField
        name="etatPotagerArrivee"
        label="État du potager à l'arrivée"
        control={control}
        error={errors.etatPotagerArrivee}
        disabled={disabled}
      />
      <TextareaField
        name="actionsRealisees"
        label="Actions réalisées"
        control={control}
        error={errors.actionsRealisees}
        disabled={disabled}
        maxLength={JARDINIER_ACTIONS_REALISEES_MAX_LENGTH}
        rows={8}
        height={200}
      />
      <TextareaField
        name="legumesRecolteEtPlantations"
        label="Légumes prêts à récolter et nouvelles plantations"
        control={control}
        error={errors.legumesRecolteEtPlantations}
        disabled={disabled}
      />
      <TextareaField
        name="observationsComplementaires"
        label="Observations complémentaires"
        control={control}
        error={errors.observationsComplementaires}
        disabled={disabled}
      />

      <FormGroup check style={{marginTop: "8px"}}>
        <Controller
          name="arrosageVerifie"
          control={control}
          render={({field}) => (
            <Input
              {...field}
              type="checkbox"
              id="field-arrosageVerifie"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              disabled={disabled}
              value=""
            />
          )}
        />
        <Label check for="field-arrosageVerifie">
          Je certifie avoir vérifié le système d&apos;arrosage automatique
        </Label>
      </FormGroup>
    </>
  );
}

type TextareaFieldProps = {
  name: keyof Step1Form;
  label: string;
  control: Control<Step1Form>;
  error: FieldErrors<Step1Form>[keyof Step1Form];
  disabled: boolean;
  maxLength?: number;
  rows?: number;
  height?: number;
};

function TextareaField({
  name,
  label,
  control,
  error,
  disabled,
  maxLength = JARDINIER_FIELD_MAX_LENGTH,
  rows,
  height,
}: TextareaFieldProps) {
  return (
    <FormGroup floating style={{marginTop: "16px"}}>
      <Controller
        name={name}
        control={control}
        render={({field}) => (
          <Input
            {...field}
            type="textarea"
            id={`field-${name}`}
            placeholder={label}
            disabled={disabled}
            maxLength={maxLength}
            rows={rows}
            style={height ? {height} : undefined}
            value={(field.value as string) ?? ""}
          />
        )}
      />
      <Label for={`field-${name}`}>{label}</Label>
      {error && (
        <p style={{color: "red", fontSize: "12px", margin: 0}}>{error.message as string}</p>
      )}
    </FormGroup>
  );
}
